import React, { useEffect, useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// Full & final settlement of a professional who left: their last pay up to
// the last working day, minus booking cash they hold and kit not returned,
// plus/minus anything else. One admin prepares it, another settles it.

const SETTLEMENT_FIELDS = `
  id
  employeeId
  fullName
  leavingDate
  periodStart
  periodEnd
  salary
  commission
  leaveDeduction
  adjustmentTotal
  cashInHand
  kitCharges
  kitNote
  otherAmount
  otherNote
  net
  details
  status
  settledAt
  paymentMode
  paymentReference
`;

const GET_SETTLEMENT = gql`
  query GetEmployeeSettlement($employeeId: ID!) {
    getEmployeeSettlement(employeeId: $employeeId) {
      status
      message
      settlement { ${SETTLEMENT_FIELDS} }
    }
  }
`;
const PREPARE = gql`
  mutation PrepareSettlement($data: PrepareSettlementInput!) {
    prepareSettlement(data: $data) {
      status
      message
    }
  }
`;
const SETTLE = gql`
  mutation SettleSettlement($data: SettleSettlementInput!) {
    settleSettlement(data: $data) {
      status
      message
    }
  }
`;
const CANCEL = gql`
  mutation CancelSettlement($id: ID!) {
    cancelSettlement(id: $id) {
      status
      message
    }
  }
`;

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const Row = ({ label, value, sign, strong }) => (
  <div className={`flex justify-between py-1 ${strong ? "font-semibold text-gray-900 border-t mt-1 pt-2" : ""}`}>
    <span>{label}</span>
    <span className={sign === "-" ? "text-red-700" : sign === "+" ? "text-green-700" : ""}>
      {sign === "-" ? "-" : sign === "+" ? "+" : ""}
      {inr(Math.abs(value))}
    </span>
  </div>
);

const FullFinalSettlement = ({ employeeId }) => {
  const { data, loading, refetch } = useQuery(GET_SETTLEMENT, { variables: { employeeId }, fetchPolicy: "network-only" });
  const [prepare] = useMutation(PREPARE);
  const [settle] = useMutation(SETTLE);
  const [cancel] = useMutation(CANCEL);
  const res = data?.getEmployeeSettlement;
  const s = res?.settlement;
  const [kitCharges, setKitCharges] = useState("");
  const [kitNote, setKitNote] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [otherNote, setOtherNote] = useState("");
  const [payMode, setPayMode] = useState("BANK_TRANSFER");
  const [payRef, setPayRef] = useState("");

  useEffect(() => {
    if (!s) return;
    setKitCharges(s.kitCharges ? String(s.kitCharges) : "");
    setKitNote(s.kitNote || "");
    setOtherAmount(s.otherAmount ? String(s.otherAmount) : "");
    setOtherNote(s.otherNote || "");
  }, [s]);

  const call = async (fn, variables, after) => {
    try {
      const out = Object.values((await fn({ variables })).data)[0];
      alert(out.message);
      if (out.status === "SUCCESS") {
        if (after) after();
        refetch();
      }
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (res?.status === "ERROR") return <p className="text-sm text-red-600 bg-white rounded-xl border p-4">{res.message}</p>;
  if (!s) return null;

  const settled = s.status === "SETTLED";
  const months = s.details?.months || [];
  const cashBookings = s.details?.cashBookings || [];
  const kitItems = s.details?.kitItems || [];

  return (
    <div className="bg-white rounded-xl border p-4 space-y-4 text-sm text-gray-700">
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <div className="font-semibold text-gray-900">
            Full &amp; final · last working day {s.leavingDate}
          </div>
          <div className="text-xs text-gray-500">
            Covers {s.periodStart} to {s.periodEnd} (not paid by any locked payroll)
          </div>
        </div>
        <span
          className={`h-fit px-2 py-0.5 rounded text-xs font-semibold ${
            settled ? "bg-green-100 text-green-800" : s.status === "DRAFT" ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {settled ? "Settled" : s.status === "DRAFT" ? "Prepared - waiting to be settled" : "Not prepared yet"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Row label="Salary up to the last day" value={s.salary} sign="+" />
          <Row label="Commission" value={s.commission} sign="+" />
          <Row label="Leave deductions" value={s.leaveDeduction} sign="-" />
          <Row label="Adjustments" value={s.adjustmentTotal} sign={s.adjustmentTotal >= 0 ? "+" : "-"} />
          <Row label={`Booking cash they hold (${cashBookings.length} bookings)`} value={s.cashInHand} sign="-" />
          <Row label={`Kit / inventory charges${s.kitNote ? ` - ${s.kitNote}` : ""}`} value={s.kitCharges} sign="-" />
          <Row label={`Other${s.otherNote ? ` - ${s.otherNote}` : ""}`} value={s.otherAmount} sign={s.otherAmount >= 0 ? "+" : "-"} />
          <Row label={s.net >= 0 ? "Company pays them" : "They pay the company"} value={s.net} strong />
          {settled && (
            <p className="text-xs text-gray-500 mt-2">
              Settled {new Date(s.settledAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" })} ·{" "}
              {s.paymentMode || "-"} {s.paymentReference ? `· ${s.paymentReference}` : ""}
            </p>
          )}
        </div>
        <div className="space-y-3">
          {months.map((m) => (
            <div key={m.month} className="text-xs">
              <div className="font-semibold text-gray-800">
                {m.month}: {m.activeDays} days, {m.bookings} bookings
              </div>
              <div className="text-gray-500">
                salary {inr(m.salary)} · commission {inr(m.commission)} · leave -{inr(m.leaveDeduction)} · adjustments{" "}
                {inr(m.adjustmentTotal)}
              </div>
            </div>
          ))}
          <div className="text-xs">
            <div className="font-semibold text-gray-800">Kit still assigned ({kitItems.length})</div>
            <div className="text-gray-500">
              {kitItems.length ? kitItems.map((k) => `${k.name}${k.quantity > 1 ? ` ×${k.quantity}` : ""}`).join(", ") : "None"}
            </div>
          </div>
        </div>
      </div>

      {!settled && (
        <div className="border-t pt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            Kit / inventory charge (₹)
            <input type="number" min="0" value={kitCharges} onChange={(e) => setKitCharges(e.target.value)} className="block border rounded px-2 py-1 mt-1 w-full" />
          </label>
          <label className="block">
            Kit note
            <input value={kitNote} onChange={(e) => setKitNote(e.target.value)} placeholder="e.g. Kit #12 not returned" className="block border rounded px-2 py-1 mt-1 w-full" />
          </label>
          <label className="block">
            Other (₹, minus to deduct)
            <input type="number" value={otherAmount} onChange={(e) => setOtherAmount(e.target.value)} className="block border rounded px-2 py-1 mt-1 w-full" />
          </label>
          <label className="block">
            Other note
            <input value={otherNote} onChange={(e) => setOtherNote(e.target.value)} placeholder="e.g. leave encashment, deposit back" className="block border rounded px-2 py-1 mt-1 w-full" />
          </label>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <button
              onClick={() =>
                call(prepare, {
                  data: {
                    employeeId,
                    kitCharges: Number(kitCharges) || 0,
                    kitNote: kitNote || null,
                    otherAmount: Number(otherAmount) || 0,
                    otherNote: otherNote || null,
                  },
                })
              }
              className="px-4 py-2 rounded bg-gray-800 text-white text-sm font-semibold"
            >
              {s.status === "DRAFT" ? "Update prepared statement" : "Prepare statement"}
            </button>
            {s.status === "DRAFT" && (
              <button onClick={() => call(cancel, { id: s.id })} className="px-4 py-2 rounded border text-sm font-semibold text-gray-700">
                Cancel draft
              </button>
            )}
          </div>
        </div>
      )}

      {s.status === "DRAFT" && (
        <div className="border-t pt-4 flex flex-wrap items-end gap-3">
          <div className="text-xs text-gray-500 w-full">
            Settling settles all their booking cash, closes their pay for these months (payroll won't pay them again) and
            records the payment. With maker-checker on, a different admin from the one who prepared it settles it.
          </div>
          <label className="text-sm">
            Mode
            <select value={payMode} onChange={(e) => setPayMode(e.target.value)} className="block border rounded px-2 py-1 mt-1">
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="UPI">UPI</option>
              <option value="CASH">Cash</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <label className="text-sm">
            Reference (UTR){payMode === "CASH" ? " - optional" : ""}
            <input value={payRef} onChange={(e) => setPayRef(e.target.value)} className="block border rounded px-2 py-1 mt-1 font-mono" />
          </label>
          <button
            onClick={() => {
              if (window.confirm(`Settle: ${s.net >= 0 ? `pay ${inr(s.net)}` : `collect ${inr(-s.net)}`}?`)) {
                call(settle, { data: { settlementId: s.id, paymentMode: payMode, paymentReference: payRef || null } });
              }
            }}
            className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold"
          >
            Settle
          </button>
        </div>
      )}
    </div>
  );
};

export default FullFinalSettlement;
